/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import * as ConfigurationLocationSelector from './configuration-location-selector';
import { Store } from '@ngrx/store';
import { CallsService } from '../../../../../utils/src/lib/backend-calls/calls-backend-service';
import { from, map, Observable, of } from 'rxjs';
import { GetFolderPath } from './configuration-location-actions';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { IConfigurationLocationFacade } from '../../../../../data-access/src/lib/domain/facades-models/change-configuration-location-facade';
import { GlobalConstants } from '../../constants/global-constants';
import {
  ConfigurationLocationApplyResult,
  ConfigurationLocationPath,
  ConfigurationLocationPathPicker,
  ConfigurationLocationReturnCodes,
} from '../../models/configuration-location-return-codes';
import { SystemStateManager } from '../../../../../utils/src/lib/system-state-manager.service';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationLocationFacade implements IConfigurationLocationFacade {
  constructor(
    private store: Store,
    private backendCallsService: CallsService,
    private localStorage: LocalStorageService,
    private stateManager: SystemStateManager
  ) {}

  public getDockedState(): SysqueElectricalState {
    return this.stateManager.getDockedModeState();
  }

  public verifyDirectory(path: string): Observable<ConfigurationLocationReturnCodes> {
    return from<Promise<ConfigurationLocationReturnCodes>>(
      this.backendCallsService.messageHandler.sendRequestAsync('VerifyDirectory', [path])
    );
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorage.updateDispatchLocalStorage(key, value);
  }

  public closeWindow(): Observable<boolean> {
    return this.backendCallsService.onCloseWindow(GlobalConstants.OnCloseWindow);
  }

  public windowResize(height: number): void {
    this.backendCallsService.onWindowResize(GlobalConstants.ChangeHeight, height).subscribe();
  }

  public folderSelection(): Observable<ConfigurationLocationPathPicker> {
    this.store.dispatch(GetFolderPath());
    return this.store
      .select(ConfigurationLocationSelector.selectFolderPath)
      .pipe(
        map((response) => ({
          path: response.folderPath,
          returnCode: response.statusCode
        }))
      );
  }

  public revertConfigurationLocation(): Observable<string> {
    return from<Promise<string>>(
      this.backendCallsService.messageHandler.sendRequestAsync(GlobalConstants.RevertConfigurationLocation)
    );
  }

  public getFolderPath(): Observable<ConfigurationLocationPath> {
    return from<Promise<ConfigurationLocationPath>>(
      this.backendCallsService.messageHandler.sendRequestAsync(GlobalConstants.GetConfigurationLocationPath)
    );
  }

  public applyNewConfigurationLocation(
    newFolderPath: string,
    isTetherConfiguration: boolean
  ): Observable<ConfigurationLocationApplyResult> {
    return from<Promise<ConfigurationLocationApplyResult>>(
      this.backendCallsService.messageHandler.sendRequestAsync(GlobalConstants.ApplyConfigurationLocationPath, [
        newFolderPath,
        isTetherConfiguration,
      ])
    );
  }

  public setTetheredConfiguration(path: string): Observable<any> {
    return from<Promise<any>>(
      this.backendCallsService.messageHandler.sendRequestAsync(GlobalConstants.SetTetherConfigurationLocationPath, [
        path,
      ])
    );
  }
}
